import { forwardRef, type HTMLAttributes } from 'react';

import styles from './CountBadge.module.css';

/**
 * CountBadge — a number worth interrupting someone for, or nothing at all.
 *
 * Handoff, "Tags & badges" 3 and the migration map: "A zero badge is hidden,
 * not rendered as '0' (today's build shows a permanent red '0' in the topbar)."
 * That is the whole reason this component exists as a component: `{count > 0 &&
 * <span>{count}</span>}` at every call site is one forgotten guard away from
 * shipping the bug back.
 *
 * `label` is required, and that is a deviation from the plan's
 * `<CountBadge count tone />`. A bare "7" is not an accessible name — next to a
 * bell icon a screen reader reads "Notifications, seven", which is a fact
 * without a unit. The digits are therefore hidden and a caller-translated
 * phrase ("7 unread requests") is what reaches assistive technology. Hiding the
 * digits rather than adding `aria-label` to the span is deliberate: `aria-label`
 * on a role-less element is ignored outright by several screen readers.
 */

export type CountBadgeTone = 'neutral' | 'alert';

export interface CountBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  count: number;
  /**
   * The accessible name — a translated phrase naming what is being counted.
   * Required: "7" on its own says nothing.
   */
  label: string;
  /** Handoff: red only for items needing action. */
  tone?: CountBadgeTone;
}

export const CountBadge = forwardRef<HTMLSpanElement, CountBadgeProps>(function CountBadge(
  { count, label, tone = 'neutral', className, ...rest },
  ref,
) {
  // Not `count === 0`: a negative count is a bug upstream, and rendering "-1"
  // in a red circle turns it into a bug the agent has to interpret.
  if (!Number.isFinite(count) || count <= 0) return null;

  return (
    <span
      {...rest}
      ref={ref}
      className={[styles['badge'], styles[tone], className].filter(Boolean).join(' ')}
      data-tone={tone}
    >
      <span aria-hidden="true">{count}</span>
      <span className={styles['srOnly']}>{label}</span>
    </span>
  );
});
